import { Col } from 'react-bootstrap';
import AirbnbIcon from 'assets/images/icons/channels/airbnb.svg?react';
import { useTranslation } from 'react-i18next';
import { normalizeKey } from 'utils/localeUtils';
import Tooltip from 'components/tooltip/Tooltip';
import QuestionCircleHelp from 'assets/images/question-circle.svg?react';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import useFormatNumber from 'hooks/useFormatNumber';
import { HostingQualitySubtitle } from '../HostingQualityWrapper.styles';
import useAirbnbKeyMetrics from './useAirbnbKeyMetrics';
import { AirbnbAnalyticsKeyMetricsResponse } from './types/HostQuality.types';
import {
  ReviewQualityListItemHeader,
  ReviewQualityListItemValue,
  ReviewQualityListItemWrapper,
  ReviewQualityListWrapper,
} from './ReviewsQuality.styles';

const ReviewsQuality = () => {
  const { keyMetrics, isKeyMetricsLoading } = useAirbnbKeyMetrics();
  const { t } = useTranslation();
  const formatNumber = useFormatNumber();

  const categories: (keyof AirbnbAnalyticsKeyMetricsResponse)[] = [
    'overallReviewScore',
    'fiveStarReviewRate',
    'cleanliness',
    'accuracy',
    'checkin',
    'communication',
    'location',
    'value',
  ];

  const percentageCategories: (keyof AirbnbAnalyticsKeyMetricsResponse)[] = [
    'fiveStarReviewRate',
  ];

  const colors = {
    GREEN: '#198754',
    ORANGE: '#FD7E14',
    RED: '#DC3545',
  };

  const getColorByValue = (
    category: keyof AirbnbAnalyticsKeyMetricsResponse,
    value: number | null | undefined,
  ): string => {
    if (percentageCategories.includes(category)) {
      if (value === null || value === undefined) {
        return colors.GREEN;
      }

      if (value >= 0.9) {
        return colors.GREEN;
      }

      if (value >= 0.76) {
        return colors.ORANGE;
      }

      return colors.RED;
    }

    if (value === null || value === undefined) {
      return colors.GREEN;
    }
    if (value >= 4.7) {
      return colors.GREEN;
    }

    if (value >= 4.3) {
      return colors.ORANGE;
    }

    return colors.RED;
  };

  const displayMetricBasedOnCategory = (
    metric: AirbnbAnalyticsKeyMetricsResponse,
    category: keyof AirbnbAnalyticsKeyMetricsResponse,
  ) => {
    if (metric[category] === null || metric[category] === undefined) {
      return '';
    }

    if (percentageCategories.includes(category)) {
      return `${formatNumber({
        value: metric[category]! * 100,
      })}%`;
    }

    return formatNumber({
      value: metric[category]!,
    });
  };

  return (
    <Col xs={6}>
      <HostingQualitySubtitle>
        <b>{t('pageAnalytics.reviewsQualityHeader')}</b> <AirbnbIcon />
      </HostingQualitySubtitle>

      <ReviewQualityListWrapper data-testid="review-quality">
        {categories.map((category) => (
          <ReviewQualityListItemWrapper
            key={category}
            data-testid={`review-quality-${category}`}
          >
            <ReviewQualityListItemHeader>
              {t(normalizeKey(`pageAnalytics.reviewsQuality.${category}`))}
              <Tooltip
                text={t(
                  normalizeKey(`pageAnalytics.reviewsQuality.${category}Help`),
                )}
                id={`tooltip-${category}-help`}
              >
                <QuestionCircleHelp className="help-icon" />
              </Tooltip>
            </ReviewQualityListItemHeader>
            <ReviewQualityListItemValue
              color={getColorByValue(category, keyMetrics?.[category])}
            >
              {isKeyMetricsLoading && (
                <LoadingPlaceholder linesCount={1} width="100px" />
              )}
              {keyMetrics && keyMetrics[category] === null && '--'}
              {!isKeyMetricsLoading &&
                keyMetrics &&
                displayMetricBasedOnCategory(keyMetrics, category)}
            </ReviewQualityListItemValue>
          </ReviewQualityListItemWrapper>
        ))}
      </ReviewQualityListWrapper>
    </Col>
  );
};

export default ReviewsQuality;
